import React, { useMemo, useCallback } from 'react';
import { View, StyleSheet, Pressable, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ToolPageCopy from '@/components/Tools/ToolPageCopy';
import { isGuideHowToBlock } from '@/utils/topic_page_copy';
import { snippetExcludeWebProps } from '@/utils/topic_seo';

function asQuestion(text) {
  const q = String(text || '').trim();
  if (!q) return '';
  return q.endsWith('?') ? q : `${q}?`;
}

const CONTINUE_VARIANTS = [
  { key: 'continueAskMore', icon: 'arrow-up-right', usesModel: true },
  { key: 'continueDigDeeper', icon: 'sparkles', usesModel: false },
  { key: 'continueExplore', icon: 'compass', usesModel: false },
];

function TopicInlineContinue({ label, icon, accent, onPress }) {
  const styles = useMemo(() => StyleSheet.create({
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 999,
      borderWidth: StyleSheet.hairlineWidth,
      ...(Platform.OS === 'web' ? { cursor: 'pointer' } : null),
    },
    label: {
      lineHeight: 18,
    },
  }), []);

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={({ pressed, hovered }) => [
        styles.chip,
        {
          backgroundColor: `${accent}12`,
          borderColor: `${accent}38`,
        },
        Platform.OS === 'web' && hovered && !pressed && {
          backgroundColor: `${accent}22`,
          borderColor: `${accent}58`,
        },
        pressed && { opacity: 0.86, transform: [{ scale: 0.98 }] },
      ]}
    >
      <Text tag="span" size="s" weight="semibold" style={[styles.label, { color: accent }]}>
        {label}
      </Text>
      <Icon name={icon} size={10} color={accent} solid />
    </Pressable>
  );
}

function TopicAnswerWithContinue({ copy, accent, modelName, variantIndex, onContinue, t, speakableMark = null }) {
  const { colors } = useTheme();
  const variant = CONTINUE_VARIANTS[variantIndex % CONTINUE_VARIANTS.length];
  const label = t(`pages.topics.${variant.key}`, variant.usesModel ? { model: modelName } : undefined);

  const styles = useMemo(() => StyleSheet.create({
    row: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'baseline',
      width: '100%',
    },
    prose: {
      lineHeight: 24,
      color: colors.text_color_primary,
      flexShrink: 1,
    },
    chipSlot: {
      marginTop: 15,
    },
  }), [colors]);

  if (!copy) return null;

  return (
    <View style={styles.row}>
      <Text
        tag="p"
        size="m"
        color="primary"
        style={styles.prose}
        {...(speakableMark && Platform.OS === 'web' ? { 'data-speakable': speakableMark } : {})}
      >
        {copy}
        {' '}
      </Text>
      <View style={styles.chipSlot} {...snippetExcludeWebProps()}>
        <TopicInlineContinue
          label={label}
          icon={variant.icon}
          accent={accent}
          onPress={onContinue}
        />
      </View>
    </View>
  );
}

function TopicUserQuestion({ question, onPress }) {
  const { colors } = useTheme();
  const styles = useMemo(() => StyleSheet.create({
    row: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      width: '100%',
    },
    pressable: {
      maxWidth: '100%',
      alignSelf: 'flex-end',
    },
    bubble: {
      maxWidth: '100%',
      alignSelf: 'flex-end',
      backgroundColor: colors.solid_color_user_bubble,
      borderRadius: 20,
      paddingHorizontal: 14,
      paddingVertical: 10,
      ...(Platform.OS === 'web' ? { cursor: onPress ? 'pointer' : 'default' } : null),
    },
    text: {
      color: colors.text_color_user_bubble || colors.text_color_primary,
      lineHeight: 24,
      flexShrink: 1,
    },
  }), [colors, onPress]);

  const body = (
    <View style={styles.bubble}>
      <Text tag="h2" size="l" style={styles.text}>{question}</Text>
    </View>
  );

  if (!onPress) {
    return <View style={styles.row} {...snippetExcludeWebProps()}>{body}</View>;
  }

  return (
    <View style={styles.row} {...snippetExcludeWebProps()}>
      <Pressable
        onPress={() => onPress(question)}
        accessibilityRole="button"
        style={({ pressed }) => [styles.pressable, pressed && { opacity: 0.88 }]}
      >
        {body}
      </Pressable>
    </View>
  );
}

function TopicModelAnswer({
  modelName,
  modelLogoPath,
  children,
}) {
  const { colors } = useTheme();
  const styles = useMemo(() => StyleSheet.create({
    block: {
      width: '100%',
      gap: 8,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    avatar: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: colors.solid_color_tertiary,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    body: {
      paddingLeft: 36,
      gap: 8,
    },
  }), [colors]);

  return (
    <View style={styles.block}>
      <View style={styles.header} {...snippetExcludeWebProps()}>
        <View style={styles.avatar}>
          {modelLogoPath ? (
            <Image source={{ uri: modelLogoPath }} style={{ width: 28, height: 28 }} contentFit="cover" />
          ) : (
            <Icon name="sparkles" size={14} color={colors.text_color_secondary} solid />
          )}
        </View>
        <Text tag="span" size="s" weight="bold" color="primary">{modelName}</Text>
      </View>
      <View style={styles.body}>{children}</View>
    </View>
  );
}

function TopicSectionExchange({
  title,
  copy,
  modelName,
  modelLogoPath,
  accent,
  variantIndex,
  onQuestionPress,
  onContinuePress,
  t,
  speakableMark = null,
}) {
  const question = asQuestion(title);

  const handleContinue = useCallback(() => {
    onContinuePress?.(title);
  }, [onContinuePress, title]);

  if (!question || !copy) return null;

  return (
    <View style={{ gap: 12 }}>
      <TopicUserQuestion question={question} onPress={onQuestionPress} />
      <TopicModelAnswer modelName={modelName} modelLogoPath={modelLogoPath}>
        <TopicAnswerWithContinue
          copy={copy}
          accent={accent}
          modelName={modelName}
          variantIndex={variantIndex}
          onContinue={handleContinue}
          t={t}
          speakableMark={speakableMark}
        />
      </TopicModelAnswer>
    </View>
  );
}

function TopicListExchange({
  title,
  items,
  ordered,
  modelName,
  modelLogoPath,
  accent,
  variantIndex,
  onQuestionPress,
  onContinuePress,
  t,
}) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  const question = asQuestion(title || 'How do I use this guide?');

  const handleContinue = useCallback(() => {
    onContinuePress?.(title);
  }, [onContinuePress, title]);

  if (!list.length) return null;

  const lastItemIndex = list.reduce((acc, item, index) => {
    const text = typeof item === 'string'
      ? item
      : (item.title || item.description || item.copy || item.subtitle || '');
    return text ? index : acc;
  }, -1);

  return (
    <View style={{ gap: 12 }}>
      <TopicUserQuestion question={question} onPress={onQuestionPress} />
      <TopicModelAnswer modelName={modelName} modelLogoPath={modelLogoPath}>
        <View style={{ gap: 8 }}>
          {list.map((item, index) => {
            const text = typeof item === 'string'
              ? item
              : (item.title || item.description || item.copy || item.subtitle || '');
            if (!text) return null;
            const isLast = index === lastItemIndex;
            return (
              <View key={`${index}-${text.slice(0, 24)}`} style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-start' }}>
                {ordered && (
                  <Text tag="span" size="s" weight="bold" color="secondary" style={{ width: 20, marginTop: 2 }}>
                    {index + 1}.
                  </Text>
                )}
                {isLast ? (
                  <View style={{ flex: 1 }}>
                    <TopicAnswerWithContinue
                      copy={text}
                      accent={accent}
                      modelName={modelName}
                      variantIndex={variantIndex}
                      onContinue={handleContinue}
                      t={t}
                    />
                  </View>
                ) : (
                  <Text tag="p" size="m" color="primary" style={{ flex: 1, lineHeight: 24 }}>{text}</Text>
                )}
              </View>
            );
          })}
        </View>
      </TopicModelAnswer>
    </View>
  );
}

function TopicFaqExchange({
  items,
  modelName,
  modelLogoPath,
  accent,
  baseVariantIndex,
  onQuestionPress,
  onContinuePress,
  t,
}) {
  const list = Array.isArray(items) ? items : [];
  return (
    <View style={{ gap: 28 }}>
      {list.map((item, index) => {
        const q = asQuestion(item?.question || item?.q || item?.title);
        const a = item?.answer || item?.a || item?.description || item?.copy;
        if (!q || !a) return null;
        return (
          <TopicSectionExchange
            key={`faq-${index}`}
            title={q.replace(/\?$/, '')}
            copy={a}
            modelName={modelName}
            modelLogoPath={modelLogoPath}
            accent={accent}
            variantIndex={baseVariantIndex + index}
            onQuestionPress={onQuestionPress}
            onContinuePress={onContinuePress}
            t={t}
          />
        );
      })}
    </View>
  );
}

function TopicPageCopyBlock({
  block,
  blockIndex,
  modelName,
  modelLogoPath,
  accent,
  palette,
  isMD,
  onQuestionPress,
  onContinuePress,
  t,
  speakableMark = null,
}) {
  const type = block?.type || 'section';

  if (isGuideHowToBlock(block)) {
    return (
      <View style={{ marginTop: blockIndex > 0 ? 12 : 0 }} {...snippetExcludeWebProps()}>
        <ToolPageCopy blocks={[block]} palette={palette} isMD={isMD} />
      </View>
    );
  }

  if (type === 'section') {
    return (
      <TopicSectionExchange
        title={block.title}
        copy={block.copy || block.subtitle}
        modelName={modelName}
        modelLogoPath={modelLogoPath}
        accent={accent}
        variantIndex={blockIndex}
        onQuestionPress={onQuestionPress}
        onContinuePress={onContinuePress}
        t={t}
        speakableMark={speakableMark}
      />
    );
  }

  if (type === 'ordered-list' || type === 'unordered-list') {
    return (
      <TopicListExchange
        title={block.title}
        items={block.items}
        ordered={type === 'ordered-list'}
        modelName={modelName}
        modelLogoPath={modelLogoPath}
        accent={accent}
        variantIndex={blockIndex}
        onQuestionPress={onQuestionPress}
        onContinuePress={onContinuePress}
        t={t}
      />
    );
  }

  if (type === 'faq') {
    return (
      <TopicFaqExchange
        items={block.items}
        modelName={modelName}
        modelLogoPath={modelLogoPath}
        accent={accent}
        baseVariantIndex={blockIndex}
        onQuestionPress={onQuestionPress}
        onContinuePress={onContinuePress}
        t={t}
      />
    );
  }

  return null;
}

/**
 * Topic SEO page copy as a lightweight chat thread — user question bubble,
 * then an in-house model answer (default Arya). "How to use this guide"
 * ordered-list blocks keep the classic ToolPageCopy layout.
 */
function TopicPageCopy({
  blocks,
  modelName = 'Arya',
  modelLogoPath = null,
  accent = '#6366f1',
  palette = null,
  isMD = false,
  onQuestionPress = null,
  onContinuePress = null,
}) {
  const { t } = useTranslation();
  const resolvedPalette = useMemo(
    () => palette || { fg: accent, bg: `${accent}22`, soft: `${accent}0d` },
    [palette, accent],
  );

  const styles = useMemo(() => StyleSheet.create({
    thread: {
      gap: 28,
      width: '100%',
    },
  }), []);

  if (!blocks?.length) return null;

  const speakableOverviewIndex = blocks.findIndex(
    (block) => block?.type === 'section'
      && !isGuideHowToBlock(block)
      && String(block.copy || block.subtitle || '').trim(),
  );

  return (
    <View style={styles.thread}>
      {blocks.map((block, index) => (
        <TopicPageCopyBlock
          key={`topic-copy-${index}`}
          block={block}
          blockIndex={index}
          modelName={modelName}
          modelLogoPath={modelLogoPath}
          accent={accent}
          palette={resolvedPalette}
          isMD={isMD}
          onQuestionPress={onQuestionPress}
          onContinuePress={onContinuePress}
          t={t}
          speakableMark={index === speakableOverviewIndex ? 'topic-overview' : null}
        />
      ))}
    </View>
  );
}

export default React.memo(TopicPageCopy);
